import { Navigate, useLocation, useParams } from 'react-router';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { selectRestaurant } from '../redux/slices/restaurantSlice';
import { fetchSiteContentRequest } from '../redux/slices/siteContentSlice';
import { checkAndValidateEditControl } from '../utils/editControlUtils';
import { getAppMode, isValidTemplate } from '../utils/appConfig';
import { getRestaurantId } from '../api/tokenUtils';
import { useEffect, useMemo } from 'react';

export default function RestaurantGate({
  children,
  requiresRestaurant = true,
  redirectTo = '/select-location',
}: {
  children: React.ReactNode;
  requiresRestaurant?: boolean;
  redirectTo?: string;
}) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { restaurants, selectedRestaurant } = useAppSelector((state) => state.restaurant);
  const { id } = useParams(); // Using the single 'id' parameter from router

  // Use the centralized mode detection
  const mode = getAppMode();
  const isPreviewPath = location.pathname.includes('/preview');
  const isEditPath = location.pathname.includes('/preview/edit');

  // Determine what type of ID we have
  const isTemplatePreview = useMemo(() => {
    return mode === 'preview' && id && isValidTemplate(id);
  }, [mode, id]);

  const isRestaurantEdit = useMemo(() => {
    return mode === 'edit' && id && !isValidTemplate(id);
  }, [mode, id]);

  const isRestaurantMode = useMemo(() => {
    // Only treat as restaurant mode if path actually contains /preview or /preview/edit
    // This prevents parent IDs in root path from being treated as restaurant mode
    const hasPreviewInPath = isPreviewPath || isEditPath;
    return isRestaurantEdit || (id && !isValidTemplate(id) && hasPreviewInPath);
  }, [isRestaurantEdit, id, isPreviewPath, isEditPath]);

  // Handle template preview mode
  useEffect(() => {
    if (isTemplatePreview) {
      dispatch(fetchSiteContentRequest());
    }
  }, [isTemplatePreview, id, dispatch]);

  // Handle restaurant selection from URL (edit mode or restaurant preview)
  useEffect(() => {
    if (isRestaurantMode && id && restaurants?.length) {
      const restaurant = restaurants.find(r => r.restaurant_id === id);
      if (restaurant && selectedRestaurant?.restaurant_id !== restaurant.restaurant_id) {
        dispatch(selectRestaurant(restaurant.restaurant_id));
        dispatch(fetchSiteContentRequest());
        
        // Enable edit controls if in edit mode
        if (mode === 'edit') {
          setTimeout(() => checkAndValidateEditControl(restaurant.restaurant_id), 100);
        }
      }
    }
  }, [isRestaurantMode, id, restaurants, mode, selectedRestaurant, dispatch]);

  // Handle normal mode page refresh with stored restaurant ID (e.g., /menu, /contact, etc.)
  useEffect(() => {
    if (mode === 'normal' && 
        location.pathname !== '/' && 
        !selectedRestaurant && 
        !isTemplatePreview && 
        !isRestaurantMode) {
      
      const storedRestaurantId = getRestaurantId();
      
      if (storedRestaurantId && restaurants?.length) {
        const restaurant = restaurants.find(r => r.restaurant_id === storedRestaurantId);
        if (restaurant) {
          dispatch(selectRestaurant(storedRestaurantId));
          dispatch(fetchSiteContentRequest());
        }
      }
    }
  }, [mode, location.pathname, selectedRestaurant, isTemplatePreview, isRestaurantMode, restaurants, dispatch]);

  // Skip location selector for preview/edit modes only
  // Allow "restaurant" mode to continue (it just means UUID in URL, could be parent or franchise)
  if (isTemplatePreview || isRestaurantMode || (mode !== 'normal' && mode !== 'restaurant')) {
    return <>{children}</>;
  }

  // Helper to detect root or parent ID path (UUID format)
  const isRootOrParentPath = 
    location.pathname === '/' || 
    location.pathname === '/select-location' ||
    /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(location.pathname) ||
    /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/select-location$/i.test(location.pathname);

  // Auto-select if only ONE restaurant
  if (requiresRestaurant && !selectedRestaurant && isRootOrParentPath && restaurants?.length === 1) {
    dispatch(selectRestaurant(restaurants[0].restaurant_id));
    dispatch(fetchSiteContentRequest());
    return <>{children}</>;
  }

  // DON'T auto-load saved restaurant for parent ID URLs
  // Always show LocationSelector for parent IDs, even if user has a saved restaurant
  // This ensures users can choose from all franchises under the parent

  // Helper to calculate redirect path - preserves parent ID if present
  const getRedirectPath = () => {
    // Extract parent ID from current URL
    const parentIdMatch = location.pathname.match(/^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    
    if (parentIdMatch) {
      // Preserve parent ID in redirect
      return `/${parentIdMatch[1]}/select-location`;
    }
    
    // No parent ID, use default redirectTo prop
    return redirectTo;
  };

  // Show LocationSelector if MULTIPLE restaurants and NO saved selection
  if (requiresRestaurant && 
      !selectedRestaurant && 
      isRootOrParentPath && 
      restaurants?.length > 1) {
    return <Navigate to={getRedirectPath()} replace />;
  }

  return <>{children}</>;
}
